import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPencil, IconPlus, IconSettings, IconTrash, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Expense, ExpenseAttachment, ExpenseCategory, ExpensePaymentMethod } from "../../api/types";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { ExpenseCategoriesModal } from "./ExpenseCategoriesModal";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

const METHODS: ExpensePaymentMethod[] = ["cash", "card", "transfer"];
const CURRENCIES = ["MDL", "EUR", "USD"];

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function ExpensesTab() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const isAdmin = user.role === "admin";

  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterMethod, setFilterMethod] = useState<string | null>(null);

  const [catModalOpen, setCatModalOpen] = useState(false);

  // editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [fSpentAt, setFSpentAt] = useState(todayStr());
  const [fCategory, setFCategory] = useState<string | null>(null);
  const [fAmount, setFAmount] = useState<number | string>("");
  const [fCurrency, setFCurrency] = useState("MDL");
  const [fMethod, setFMethod] = useState<string | null>(null);
  const [fPaidBy, setFPaidBy] = useState("");
  const [fDescription, setFDescription] = useState("");
  const [attachments, setAttachments] = useState<ExpenseAttachment[]>([]);
  const [saving, setSaving] = useState(false);

  async function loadCategories() {
    try {
      setCategories(await api.listExpenseCategories());
    } catch {
      /* soft-fail */
    }
  }

  async function load() {
    setLoading(true);
    try {
      const rows = await api.listExpenses({
        category_id: filterCategory ? Number(filterCategory) : undefined,
        from: from || undefined,
        to: to || undefined,
        method: filterMethod || undefined,
      });
      setExpenses(rows);
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCategories();
  }, []);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, filterCategory, filterMethod]);

  const activeCategoryOptions = categories
    .filter((c) => c.active)
    .map((c) => ({ value: String(c.id), label: c.name }));
  const allCategoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }));
  const methodOptions = METHODS.map((m) => ({ value: m, label: t(`expenses.method.${m}`) }));

  // Summary: totals per currency + per (category, currency), over the loaded list.
  const summary = useMemo(() => {
    const byCurrency = new Map<string, number>();
    const byCategory = new Map<string, number>();
    for (const e of expenses) {
      if (e.amount == null || !e.currency) continue;
      byCurrency.set(e.currency, (byCurrency.get(e.currency) ?? 0) + e.amount);
      const key = `${e.category_name ?? "—"} · ${e.currency}`;
      byCategory.set(key, (byCategory.get(key) ?? 0) + e.amount);
    }
    return {
      currencyTotals: [...byCurrency.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      categoryTotals: [...byCategory.entries()].sort((a, b) => a[0].localeCompare(b[0])),
    };
  }, [expenses]);

  function openAdd() {
    setEditId(null);
    setFSpentAt(todayStr());
    setFCategory(filterCategory ?? (activeCategoryOptions[0]?.value ?? null));
    setFAmount("");
    setFCurrency("MDL");
    setFMethod(null);
    setFPaidBy("");
    setFDescription("");
    setAttachments([]);
    setEditorOpen(true);
  }

  async function openEdit(e: Expense) {
    setEditId(e.id);
    setFSpentAt(e.spent_at);
    setFCategory(String(e.category_id));
    setFAmount(e.amount ?? "");
    setFCurrency(e.currency ?? "MDL");
    setFMethod(e.payment_method);
    setFPaidBy(e.paid_by ?? "");
    setFDescription(e.description ?? "");
    setAttachments([]);
    setEditorOpen(true);
    try {
      const detail = await api.getExpense(e.id);
      setAttachments(detail.attachments);
    } catch {
      /* soft-fail */
    }
  }

  async function save() {
    if (!fCategory || !fSpentAt) {
      notifications.show({ color: "red", message: t("expenses.pickCategoryDate") });
      return;
    }
    setSaving(true);
    try {
      const body = {
        spent_at: fSpentAt,
        category_id: Number(fCategory),
        amount: String(Number(fAmount) || 0),
        currency: fCurrency,
        description: fDescription.trim() || undefined,
        payment_method: fMethod || undefined,
        paid_by: fPaidBy.trim() || undefined,
      };
      if (editId == null) {
        await api.createExpense(body);
      } else {
        await api.updateExpense(editId, body);
      }
      notifications.show({ color: "green", message: t("expenses.savedToast") });
      setEditorOpen(false);
      void load();
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function remove(e: Expense) {
    if (!window.confirm(t("expenses.confirmDelete"))) return;
    try {
      await api.deleteExpense(e.id);
      notifications.show({ color: "green", message: t("expenses.deletedToast") });
      void load();
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  async function uploadReceipt(file: File | null) {
    if (!file || editId == null) return;
    try {
      await api.uploadExpenseAttachment(editId, file);
      const detail = await api.getExpense(editId);
      setAttachments(detail.attachments);
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  async function deleteReceipt(a: ExpenseAttachment) {
    if (editId == null) return;
    try {
      await api.deleteExpenseAttachment(editId, a.id);
      setAttachments((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  return (
    <Stack>
      <Card withBorder>
        <Group align="end" gap="sm" wrap="wrap">
          <TextInput type="date" label={t("expenses.from")} value={from} onChange={(e) => setFrom(e.currentTarget.value)} w={150} />
          <TextInput type="date" label={t("expenses.to")} value={to} onChange={(e) => setTo(e.currentTarget.value)} w={150} />
          <Select
            label={t("expenses.category")}
            data={allCategoryOptions}
            value={filterCategory}
            onChange={setFilterCategory}
            clearable
            searchable
            placeholder={t("expenses.allCategories")}
            w={200}
          />
          <Select
            label={t("expenses.methodLabel")}
            data={methodOptions}
            value={filterMethod}
            onChange={setFilterMethod}
            clearable
            placeholder={t("expenses.allMethods")}
            w={150}
          />
          {isAdmin && (
            <>
              <Button leftSection={<IconPlus size={14} />} onClick={openAdd}>
                {t("expenses.add")}
              </Button>
              <Button variant="default" leftSection={<IconSettings size={14} />} onClick={() => setCatModalOpen(true)}>
                {t("expenses.categoriesButton")}
              </Button>
            </>
          )}
        </Group>
      </Card>

      {isAdmin && (summary.currencyTotals.length > 0 || summary.categoryTotals.length > 0) && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>{t("expenses.summaryTitle")}</Text>
            <Group gap="xs">
              {summary.currencyTotals.map(([cur, tot]) => (
                <Badge key={cur} size="lg" variant="light">
                  {tot.toFixed(2)} {cur}
                </Badge>
              ))}
            </Group>
          </Group>
          <Stack gap={2}>
            {summary.categoryTotals.map(([key, tot]) => (
              <Group key={key} justify="space-between">
                <Text size="sm" c="dimmed">{key}</Text>
                <Text size="sm" ff="monospace">{tot.toFixed(2)}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      {loading ? (
        <Text c="dimmed" size="sm">{t("common.loading")}</Text>
      ) : expenses.length === 0 ? (
        <Text c="dimmed" size="sm">{t("expenses.empty")}</Text>
      ) : (
        <ResponsiveTable
          mobile={{
            rows: expenses,
            rowKey: (e) => e.id,
            title: (e) => e.category_name ?? "—",
            subtitle: (e) =>
              isAdmin && e.amount != null ? `${e.spent_at} · ${e.amount.toFixed(2)} ${e.currency}` : e.spent_at,
            badge: (e) => (e.payment_method ? <Badge variant="light">{t(`expenses.method.${e.payment_method}`)}</Badge> : null),
            fields: [
              { label: t("expenses.colDescription"), render: (e) => e.description ?? "", hidden: (e) => !e.description },
              { label: t("expenses.colPaidBy"), render: (e) => e.paid_by ?? "—" },
            ],
            actions: isAdmin
              ? (e) => (
                  <Group gap={2} wrap="nowrap">
                    <Button size="compact-md" variant="subtle" onClick={() => void openEdit(e)}><IconPencil size={16} /></Button>
                    <Button size="compact-md" variant="subtle" color="red" onClick={() => void remove(e)}><IconTrash size={16} /></Button>
                  </Group>
                )
              : undefined,
          }}
        >
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("expenses.colDate")}</Table.Th>
                <Table.Th>{t("expenses.colCategory")}</Table.Th>
                <Table.Th>{t("expenses.colDescription")}</Table.Th>
                <Table.Th>{t("expenses.colMethod")}</Table.Th>
                <Table.Th>{t("expenses.colPaidBy")}</Table.Th>
                {isAdmin && <Table.Th style={{ textAlign: "right" }}>{t("expenses.colAmount")}</Table.Th>}
                {isAdmin && <Table.Th w={80} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expenses.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>{e.spent_at}</Table.Td>
                  <Table.Td>{e.category_name ?? "—"}</Table.Td>
                  <Table.Td><Text size="sm" lineClamp={1} maw={260}>{e.description ?? "—"}</Text></Table.Td>
                  <Table.Td>{e.payment_method ? <Badge variant="light">{t(`expenses.method.${e.payment_method}`)}</Badge> : "—"}</Table.Td>
                  <Table.Td>{e.paid_by ?? "—"}</Table.Td>
                  {isAdmin && (
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text ff="monospace">{e.amount != null ? `${e.amount.toFixed(2)} ${e.currency}` : "—"}</Text>
                    </Table.Td>
                  )}
                  {isAdmin && (
                    <Table.Td>
                      <Group gap={2} wrap="nowrap">
                        <Button size="compact-xs" variant="subtle" onClick={() => void openEdit(e)}><IconPencil size={14} /></Button>
                        <Button size="compact-xs" variant="subtle" color="red" onClick={() => void remove(e)}><IconTrash size={14} /></Button>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        </ResponsiveTable>
      )}

      <Modal opened={editorOpen} onClose={() => setEditorOpen(false)} title={editId == null ? t("expenses.addTitle") : t("expenses.editTitle")} centered>
        <Stack>
          <TextInput type="date" label={t("expenses.spentAt")} value={fSpentAt} onChange={(e) => setFSpentAt(e.currentTarget.value)} />
          <Select label={t("expenses.category")} data={activeCategoryOptions} value={fCategory} onChange={setFCategory} searchable />
          <Group grow>
            <NumberInput label={t("expenses.colAmount")} value={fAmount} onChange={setFAmount} min={0} allowNegative={false} decimalScale={2} />
            <Select label={t("expenses.currency")} data={CURRENCIES} value={fCurrency} onChange={(v) => setFCurrency(v ?? "MDL")} allowDeselect={false} />
          </Group>
          <Group grow>
            <Select label={t("expenses.methodLabel")} data={methodOptions} value={fMethod} onChange={setFMethod} clearable />
            <TextInput label={t("expenses.colPaidBy")} value={fPaidBy} onChange={(e) => setFPaidBy(e.currentTarget.value)} />
          </Group>
          <Textarea label={t("expenses.colDescription")} value={fDescription} onChange={(e) => setFDescription(e.currentTarget.value)} minRows={2} />

          {editId != null && (
            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="sm" fw={500}>{t("expenses.receipts")}</Text>
                <Button size="compact-xs" variant="light" component="label" leftSection={<IconUpload size={12} />}>
                  {t("expenses.addReceipt")}
                  <input
                    type="file"
                    hidden
                    onChange={(ev) => void uploadReceipt(ev.currentTarget.files?.[0] ?? null)}
                  />
                </Button>
              </Group>
              {attachments.length === 0 ? (
                <Text size="xs" c="dimmed">{t("expenses.noReceipts")}</Text>
              ) : (
                attachments.map((a) => (
                  <Group key={a.id} justify="space-between" gap="xs">
                    <a href={api.expenseAttachmentDownloadUrl(editId, a.id)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                      {a.original_filename}
                    </a>
                    <Button size="compact-xs" variant="subtle" color="red" onClick={() => void deleteReceipt(a)}><IconTrash size={12} /></Button>
                  </Group>
                ))
              )}
            </Stack>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditorOpen(false)}>{t("common.cancel")}</Button>
            <Button loading={saving} onClick={() => void save()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <ExpenseCategoriesModal opened={catModalOpen} onClose={() => setCatModalOpen(false)} onChanged={() => void loadCategories()} />
    </Stack>
  );
}
